import { t } from '@lingui/macro'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { getAllergyEmptyMessage } from '@/modules/app/utils/allergy.utils'
import { DataPointList } from '@/modules/shared/components/data-point'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { splitText } from '../../utils/split-question-text'
import { MedicationSchemeOverview } from './medication/MedicationSchemeOverview'

import type { AllergyProfile, HealthParameter, MedicationScheme } from '@/modules/app/types/HealthParameter.types'

type Props = {
  healthParameter: HealthParameter
  shownInQuestionnaire?: boolean
}

export const QuestionObservation = ({ healthParameter }: Props) => {
  const theme = useTheme()
  // Get the date from the most recently updated value
  const updatedAt = healthParameter.lastUpdatedAt
    ? formatLocale(new Date(healthParameter.lastUpdatedAt), 'dd MMMM yyyy')
    : undefined

  const hasData = healthParameter?.observations?.length && healthParameter.observations.length > 0

  const updatedAtDescription = updatedAt
    ? t({
        id: 'observation.updatedAt',
        message: 'Ingegeven op {date}',
        values: { date: updatedAt },
      })
    : undefined

  // A medication schedule shows its lines with the photos they were read from,
  // like the submitted answers, rather than as plain data points.
  const medicationSchedule = healthParameter?.observations?.find(
    (observation) => observation.type === 'Medication' && observation.linkId !== 'medication_scheme',
  )?.value as MedicationScheme | undefined

  if (medicationSchedule) {
    return (
      <FlexColumn gap={theme.UI.Spacing.Space4}>
        {updatedAtDescription && (
          <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
            {updatedAtDescription}
          </Span>
        )}
        <MedicationSchemeOverview
          medications={(medicationSchedule.medications ?? []).filter((m) => m.diffStatus !== 'deleted')}
          schemeStatus={medicationSchedule.schemeStatus}
          files={medicationSchedule.files}
        />
      </FlexColumn>
    )
  }

  const dataPoints =
    healthParameter?.observations?.flatMap((observation) => {
      if (observation.type === 'Medication') {
        // Medication Scheme Answer (legacy)
        if (observation.linkId === 'medication_scheme') {
          let raw = observation.value
          if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw)
            } catch {
              raw = []
            }
          }
          const medications = (Array.isArray(raw) ? raw : []) as {
            name: string
            frequency: string
            intake: string
          }[]

          const NO_MEDICATION_NAMES = new Set(['geen thuismedicatie', 'ik neem geen thuismedicatie'])
          const isNoMedication =
            medications.length === 1 && NO_MEDICATION_NAMES.has(medications[0].name.toLowerCase().trim())

          if (medications.length === 0 || isNoMedication) {
            return [
              {
                label: t({
                  id: 'observation.medication.confirmed-empty',
                  message: 'Je neemt geen medicatie',
                }),
                value: '',
              },
            ]
          }

          return medications.map((m) => {
            if (!m.frequency && !m.intake) {
              return { label: m.name, value: '' }
            }
            return {
              label: m.name,
              value: `${m.frequency} - ${m.intake}`,
            }
          })
        }
      }

      if (observation.type === 'Allergy') {
        const allergyProfile = observation.value as AllergyProfile
        const allergies = (allergyProfile?.allergies ?? []).filter((a) => a.diffStatus !== 'deleted')
        if (allergies.length === 0 && allergyProfile?.schemeStatus === 'confirmed-empty') {
          return [
            {
              // Non-null: the guard above guarantees schemeStatus is
              // 'confirmed-empty', so the helper always returns a message here.
              label: getAllergyEmptyMessage(allergyProfile?.schemeStatus)!,
              value: '',
            },
          ]
        }

        return allergies.map((allergy) => ({
          label: allergy.name,
          value:
            allergy.reaction
              ?.map((r) => r.text)
              .filter(Boolean)
              .join(', ') ?? '',
        }))
      }

      const [_, description] = splitText(observation.display)

      return [
        {
          label: description,
          // Prefer the resolved human-readable text ("NSAID, Andere
          // (Ibuprofen)") over the raw value ('nsaid').
          // Guard: observation.value can be an object/array for complex
          // types — stringify to avoid rendering [object Object].
          value:
            observation.text ??
            (typeof observation.value === 'string' ? observation.value : JSON.stringify(observation.value)),
        },
      ]
    }) || []

  return (
    <FlexColumn gap={theme.UI.Spacing.Space3}>
      {hasData ? (
        <DataPointList dataPoints={dataPoints} description={updatedAtDescription} />
      ) : (
        <Span type='BodyMediumRegular' style={{ fontWeight: 400 }}>
          {t({ id: 'observation.fillOut', message: 'Vul aan' })}
        </Span>
      )}
    </FlexColumn>
  )
}
